/**
 * Applies `planTicketDeepLink` for the Dashboard: when a deep link names a
 * ticket, read its board facts once and select its own shop day (so the card
 * mounts, highlights and — with openDetails — opens its Command Center), or
 * report that it cannot be shown on any Dayboard date (the caller then offers
 * the fallback). One read per linked ticket id; no polling.
 */
import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  DEEP_LINK_TICKET_COLUMNS,
  planTicketDeepLink,
  type DeepLinkTicketFacts,
} from '@/lib/tickets/ticketDeepLinkDay';

export interface DeepLinkFallback {
  ticketId: string;
  ticketNumber: number | null;
  reason: 'not_found' | 'no_board_date' | 'not_on_board';
}

export function useTicketDeepLinkDay(
  ticketId: string | null,
  selectedDay: string,
  setSelectedDay: (day: string) => void,
  /** Only a link that asked to OPEN the ticket gets the fallback dialog. */
  wantsDetails = true,
): { fallback: DeepLinkFallback | null; clearFallback: () => void } {
  const [fallback, setFallback] = useState<DeepLinkFallback | null>(null);
  const resolved = useRef<string | null>(null);
  const selectedRef = useRef(selectedDay);
  selectedRef.current = selectedDay;
  const setDayRef = useRef(setSelectedDay);
  setDayRef.current = setSelectedDay;
  const wantsRef = useRef(wantsDetails);
  wantsRef.current = wantsDetails;

  useEffect(() => {
    // The link was consumed (params cleared): a later click on the same
    // ticket is a new request, so forget the last answer.
    if (!ticketId) { resolved.current = null; return undefined; }
    if (resolved.current === ticketId) return undefined;
    resolved.current = ticketId;
    let live = true;
    let done = false;
    void (async () => {
      const { data, error } = await (supabase as any)
        .from('tickets').select(DEEP_LINK_TICKET_COLUMNS).eq('id', ticketId).maybeSingle();
      if (!live) return;
      done = true;
      if (error) {
        // Unreadable: leave the board as it is (the card may still be here).
        console.warn('[deep-link] ticket lookup failed', error);
        return;
      }
      const row = (data ?? null) as (DeepLinkTicketFacts & { simple_ticket_number?: number | null }) | null;
      const plan = planTicketDeepLink(row, selectedRef.current);
      if (plan.kind === 'select_day') setDayRef.current(plan.day);
      else if (plan.kind === 'fallback' && wantsRef.current) {
        setFallback({ ticketId, ticketNumber: row?.simple_ticket_number ?? null, reason: plan.reason });
      }
    })();
    return () => {
      live = false;
      // Unmounted (or StrictMode re-ran) before the answer: let the next run ask again.
      if (!done && resolved.current === ticketId) resolved.current = null;
    };
  }, [ticketId]);

  return { fallback, clearFallback: () => setFallback(null) };
}
